import { HyperLinkRedirectControlPatientProfileSubTabs } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { Guid } from "@/Common/Models/Guid";
import { HistoryTab, PatientTab, ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { getRouteFromLocation } from "@/Common/Utilities/Router/RouterHistory";
import { FlowsheetSummary } from "@/Flowsheets/Models/FlowsheetSummary";
import { PatientPrescriptionTab } from "@/Patient/Components/Medication/PatientPrescriptionTab";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";
import { MedicalItemViewOrigin } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { PatientStore } from "@/Patient/Stores/PatientStore";

// used to execute an action sequentialy after another one is completed
interface ISequentialAction {
    operation?: number;
    data?: Immutable.Map<string, any>;
    handler?: (patientId: string, action: ISequentialAction) => void;
}

interface IChangeVerticalTabParams {
    patientTab: PatientTab;
    patientId: Guid;
    navigationOrigin: MedicalItemViewOrigin;
    encounterId?: Guid;
    isPrescriptionTabActive?: boolean;
    prescriptionId?: string;
    isFlowsheetTabActive?: boolean;
    flowsheetSummary?: FlowsheetSummary;
}

class PatientTabHandler extends BaseOperationHandler {
    private patientLoadedObjects: { [patientId: string]: ISequentialAction };
    private refreshSummaryObjects: { [patientId: string]: ISequentialAction };

    constructor() {
        super();

        this.patientLoadedObjects = {};
        this.refreshSummaryObjects = {};

        this.RegisterOperation(LocalPatientOperations.UI.ChangeVerticalTabs, this.changeVerticalTabHandler);
        this.RegisterOperation(LocalPatientOperations.UI.SelectActivePatientFile, this.SelectActivePatientFile);
        this.RegisterOperation(LocalPatientOperations.UI.CloseActivePatientFile, this.CloseActivePatientFile, false);
        this.RegisterOperation(LocalOperations.Portal.PortalManagement_Show, this.ShowPortalSubscriptionTab);
        this.RegisterOperation(LocalOperations.Misc.RedirectToTab, this.HandlePatientRedirect);
        this.RegisterOperation(LocalOperations.Misc.ReloadPatientSummaryAndGoToEncounter, this.HandleSummaryReloadAndGoToEncounter);
        this.RegisterOperation(LocalOperations.TabLoadEvents.MedicalSummaryLoaded, this.MedicalSummaryLoaded, false);
    }

    private changeVerticalTabHandler(request: LocalEvent, store: PatientStore): void {
        const changeVerticalTabParams: IChangeVerticalTabParams = {
            patientTab: request.Get<PatientTab>("value"),
            patientId: request.PatientId,
            navigationOrigin: request.Get<MedicalItemViewOrigin>("MedicationViewOrigin"),
            encounterId: request.Get<Guid>("encounterId"),
            isPrescriptionTabActive: request.Get<boolean>("isPrescriptionTabActive"),
            prescriptionId: request.Get<string>("prescriptionId"),
            isFlowsheetTabActive: request.Get<boolean>("isFlowsheetTabActive"),
            flowsheetSummary: request.Get<FlowsheetSummary>("flowsheetSummary")
        };

        const isMedicalItemFromOutsideNote = request.Get<boolean>("isMedicalItemFromOutsideNote") ?? store.IsMedicalItemFromOutsideNote;
        store.IsMedicalItemFromOutsideNote = isMedicalItemFromOutsideNote;

        this.changeVerticalTab(store, changeVerticalTabParams);
    }

    private changeVerticalTab(store: PatientStore, changeVerticalTabParams: IChangeVerticalTabParams, then: () => void = null): void {
        const {
            patientTab,
            patientId,
            navigationOrigin,
            encounterId,
            isPrescriptionTabActive,
            prescriptionId,
            isFlowsheetTabActive,
            flowsheetSummary
        } = changeVerticalTabParams;

        let uiModel: IPatientUIModel;
        uiModel = store.PatientUIModels[patientId];
        if (uiModel) {
            // Flowhseet tab logic
            if (isFlowsheetTabActive != null) {
                uiModel = uiModel.set("IsFlowsheetTabActive", isFlowsheetTabActive);
            }
            if (flowsheetSummary != null) {
                uiModel = uiModel.set("FlowsheetSummary", flowsheetSummary);
            }
            uiModel = this.InitPrescriptionTab(patientId, isPrescriptionTabActive, encounterId, prescriptionId, store, uiModel);
            uiModel = uiModel.set("MedicationViewOrigin", navigationOrigin);
            uiModel = uiModel.set("ActiveVerticalTab", patientTab);

            if (patientTab === PatientTab.MedicalNote) {
                // if tab selected is note, set back the boolean value to false to change from red to normal icon
                uiModel = uiModel.set("ShowRedNoteActiveTab", false);
            }

            store.PatientUIModels[patientId] = uiModel;
        }

        if (then) {
            then();
        }
    }

    private InitPrescriptionTab(
        patientId: string,
        isPrescriptionTabActive: boolean,
        encounterId: Guid,
        prescriptionId: string,
        store: PatientStore,
        uiModel: IPatientUIModel
    ): IPatientUIModel {
        if (isPrescriptionTabActive === true) {
            if (encounterId == null && !patientStore.IsMedicalItemFromOutsideNote) throw new TypeError("encounterId is null");

            // Context has changed, reset prescription tab
            if (encounterId != store.PatientPrescriptionTabModels[patientId].get("EncounterId"))
                store.PatientPrescriptionTabModels[patientId] = Immutable.fromJS(PatientPrescriptionTab.GetDefaultPatientPrescriptionTabModel());

            // Reloading a pending prescription
            if (prescriptionId != null) {
                let prescriptionTabModel = patientStore.PatientPrescriptionTabModels[patientId]; // Get Patient Model

                prescriptionTabModel = Immutable.fromJS(PatientPrescriptionTab.GetDefaultPatientPrescriptionTabModel());
                prescriptionTabModel = prescriptionTabModel.set("prescriptionId", prescriptionId);

                let newMedicationModels: Immutable.List<MedicationModel> = Immutable.List([]);

                let prescriptionGroup = patientStore.PatientMedicalSummaryModels[patientId].get("Prescriptions"); // Get Old List
                let pendingGroup = prescriptionGroup.filter(x => x.PrescriptionId == prescriptionId);
                if (pendingGroup != null && pendingGroup.count() > 0) {
                    let meds = pendingGroup.get(0).Medications;
                    if (meds != null && meds.length > 0) {
                        meds.forEach(med => {
                            let tmpMed = med.Clone(false, false, true);
                            if (tmpMed.PrescriptionTab_OriginalMedication != null) {
                                tmpMed.PrescriptionTab_OriginalMedication.Model.PrescriptionTab_PendingStatus = tmpMed.PrescriptionTab_OriginalMedication.Model.PrescriptionStatusInt == null ? null : tmpMed.PrescriptionTab_OriginalMedication.Model.PrescriptionStatusInt.toString(); // Copy Status to PendingStatus for prescription
                            }

                            newMedicationModels = newMedicationModels.reverse().toList().push(tmpMed).reverse().toList();
                        });
                    }
                }

                prescriptionTabModel = prescriptionTabModel.set("MedicationModels", newMedicationModels); // Set new List
                patientStore.PatientPrescriptionTabModels[patientId] = prescriptionTabModel; // Replace patient Model;
            }

            store.PatientPrescriptionTabModels[patientId] = store.PatientPrescriptionTabModels[patientId].set("EncounterId", encounterId);
            uiModel = uiModel.set("IsPrescriptionTabActive", true);
        }
        else if (isPrescriptionTabActive === false) {
            store.PatientPrescriptionTabModels[patientId] = store.PatientPrescriptionTabModels[patientId].set("EncounterId", null);
            uiModel = uiModel.set("IsPrescriptionTabActive", false);
        }

        return uiModel;
    }

    private SelectActivePatientFile(request: LocalEvent, store: PatientStore): void {
        const contactId = request.Get<Guid>("contactId");
        store.setActivePatientUI(contactId, true);
    }

    private CloseActivePatientFile(request: LocalEvent, store: PatientStore): void {
        const redirect = request.Get<boolean>("redirect");
        store.IsLoading = false;
        store.closeCurrentPatientFile(redirect);
    }

    private ShowPortalSubscriptionTab(request: LocalEvent, store: PatientStore): void {
        let uiModel: IPatientUIModel;
        uiModel = store.PatientUIModels[request.PatientId];
        if (typeof uiModel !== "undefined") {
            uiModel = uiModel.set("ActiveVerticalTab", PatientTab.Profile);
            uiModel = uiModel.set("PatientProfileActiveTab", ProfileTab.PortalSubscription);
            store.PatientUIModels[request.PatientId] = uiModel;
            store.ActionsPortalManagement.loadPortalAccountOverview(request.PatientId);
        }
    }

    private HandlePatientRedirect(request: LocalEvent, store: PatientStore): void {
        const target = request.Get<RedirectTo>("target");

        let afterPatientLoadAction = null;
        const patientId = request.Get<Guid>("value");

        if (target === RedirectTo.Patient) {
            // Create action to take to activate "redirect to encounter"
            const openEncounterId = request.Get<string>("OpenEncounterId");
            const encounterEdit = request.Get<boolean>("encounterEdit");
            const taskId = request.Get<string>("taskId");
            const navigationOrigin = request.Get<MedicalItemViewOrigin>("MedicalItemViewOrigin");
            const navigateToSubTab = request.Get<HyperLinkRedirectControlPatientProfileSubTabs>("navigateToSubTab");

            if (openEncounterId != null) {
                let d = Immutable.Record({
                    encounterId: openEncounterId,
                    openInEditMode: encounterEdit
                });

                let actionToTake: ISequentialAction = {
                    operation: LocalPatientOperations.MedicalSummary.EncounterClick,
                    data: new d(),
                    handler: this.RedirectToEncounter.bind(this)
                };

                // set or overwrite
                this.patientLoadedObjects[patientId] = actionToTake;
            }

            if (taskId) {
                afterPatientLoadAction = () => this.changeVerticalTab(
                    store,
                    {
                        patientTab: PatientTab.History,
                        patientId,
                        navigationOrigin
                    },
                    () => {
                        window.setTimeout(() => {
                            let uiModel = store.PatientUIModels[patientId];
                            uiModel = uiModel.set("PatientHistoryActiveTab", HistoryTab.Task);
                            store.PatientUIModels[patientId] = uiModel;
                        }, 200);

                        sessionStore.dispatch(LocalPatientOperations.History.Task_ShowSelectedTaskFromRedirect, { taskId: taskId });
                    }
                );
            }

            if (navigateToSubTab) {
                afterPatientLoadAction = () => this.changeVerticalTab(
                    store,
                    {
                        patientTab: navigateToSubTab.secondaryTab,
                        patientId,
                        navigationOrigin
                    },
                    () => {
                        window.setTimeout(() => {
                            let uiModel = store.PatientUIModels[patientId];
                            uiModel = uiModel.set("PatientProfileActiveTab", navigateToSubTab.thernaryTab);
                            store.PatientUIModels[patientId] = uiModel;
                        }, 200);

                        sessionStore.dispatch(navigateToSubTab.operation);
                    }
                );
            }

            // Load Patient
            window.setTimeout(() => {
                let sourceLocationInfo = request.Get<Location>("currentLocation");
                const route = getRouteFromLocation(sourceLocationInfo);
                patientStore.savePatientClosedRedirectLocation(patientId, route);
                let change = patientId !== request.PatientId;
                patientStore.setActivePatientUI(patientId, change, afterPatientLoadAction);
            }, 10);
        }
    }

    private HandleSummaryReloadAndGoToEncounter(request: LocalEvent): void {
        let encounterId = request.Get<string>("encounterId");
        let encounterEdit = request.Get<boolean>("encounterEdit");

        // Create action to take to activate "redirect to encounter"
        if (encounterId != null) {
            let d = Immutable.Record({
                encounterId: encounterId,
                openInEditMode: encounterEdit
            });

            let actionToTake: ISequentialAction = {
                operation: LocalPatientOperations.MedicalSummary.EncounterClick,
                data: new d(),
                handler: this.RedirectToEncounter.bind(this)
            };
            // set or overwrite
            this.refreshSummaryObjects[request.PatientId] = actionToTake;

            // reload MedicalSummary
            window.setTimeout(() => {
                if (sessionStore.hasRightsForAction([EmployeeType.Practician]))
                    patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(request.PatientId, null, true, true, "PatientTabHandler_ReloadPatientSummaryAndGoToEncounterFromLabResult");
            }, 10);
        }
    }

    private MedicalSummaryLoaded(request: LocalEvent): void {
        let id = request.Get<string>("id");
        // deal with sequential actions if any
        if (this.patientLoadedObjects[id] != null) {
            let action = this.patientLoadedObjects[id] as ISequentialAction;
            let fn = action.handler;
            if (fn != null) {
                fn.call(this, id, action);
            }
            delete this.patientLoadedObjects[id]; // cleanup
        }

        // deal with sequential actions if any
        if (this.refreshSummaryObjects[id] != null) {
            let action = this.refreshSummaryObjects[id] as ISequentialAction;
            let fn = action.handler;
            if (fn != null) {
                fn.call(this, id, action);
            }
            delete this.refreshSummaryObjects[id]; // cleanup
        }
    }

    private RedirectToEncounter(patientId: string, action: ISequentialAction): void {
        window.setTimeout(() => {
            let op = new LocalEvent(action.operation);
            op.AddItem("value", action.data.get("encounterId"));
            op.AddItem("openInEditMode", action.data.get("openInEditMode"));
            LocalEventDispatcher.dispatch(op);
        }, 100);
    }
}

export { PatientTabHandler };
